import React from "react"
import "./CookieBanner.css"

/**
 * @typedef {'us' | 'canada' | 'international'} ConsentRegion
 */

/**
 * @typedef {Object} CookieBannerProps
 * @property {() => void} onAccept - Called when user accepts cookies
 * @property {() => void} onReject - Called when user rejects non-essential cookies
 * @property {() => void} onClose - Called when banner is closed
 * @property {ConsentRegion} region - User's detected region
 * @property {boolean} visible - Whether banner is visible
 */

const PRIVACY_POLICY_URL = "https://campusiq.com/privacy-policy"

const US_TEXT = {
  message:
    "We use cookies to enhance your CampusIQ experience and analyze product usage. Your data is never sold. By continuing, you accept our use of cookies.",
  buttonText: "Continue",
}

const INTERNATIONAL_TEXT = {
  message:
    "We use cookies to improve your CampusIQ experience and analyze product usage. You can accept or reject non-essential cookies. Your data is never sold.",
  acceptText: "Accept All",
  rejectText: "Reject Non-Essential",
}

/**
 * Cookie consent banner that adapts based on user region.
 * US users see notice-only banner.
 * International users see opt-in banner with Accept/Reject buttons.
 *
 * @param {CookieBannerProps} props - Component props
 * @returns {React.ReactElement|null} The banner component or null if not visible
 */
export function CookieBanner({ onAccept, onReject, onClose, region, visible }) {
  if (!visible) {
    return null
  }

  const isUS = region === "us"

  return (
    <div className="cookie-banner" role="dialog" aria-label="Cookie consent">
      <div className="cookie-banner__content">
        <p className="cookie-banner__message">
          {isUS ? US_TEXT.message : INTERNATIONAL_TEXT.message}{" "}
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="cookie-banner__link"
          >
            Learn more
          </a>
          .
        </p>
        <div className="cookie-banner__actions">
          {isUS ? (
            <button type="button" className="cookie-banner__button cookie-banner__button--primary" onClick={onAccept}>
              {US_TEXT.buttonText}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="cookie-banner__button cookie-banner__button--secondary"
                onClick={onReject}
              >
                {INTERNATIONAL_TEXT.rejectText}
              </button>
              <button
                type="button"
                className="cookie-banner__button cookie-banner__button--primary"
                onClick={onAccept}
              >
                {INTERNATIONAL_TEXT.acceptText}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
